import { pagesQueriesLink, urlInspectionLink } from '../GscTools/links';
import { reportsPosition, rowMetrics } from '../GscTools/metrics';
import { REVIEW, STATE, evaluatePages, navigationReason } from './rule';

/**
 * The figures the blocks draw, derived from what came back rather than fetched again. Everything
 * here re-runs when the rule changes, which is the whole point: the evaluations are kept as the
 * rows Search Console returned, and the rule is read over them on every render.
 */

/** Block 01's shortlist: the retrieved queries, most impressions first. */
export function shortlistRows(scope, rows) {
    const hasPosition = reportsPosition(scope.searchType);

    return (rows ?? [])
        .map((row) => {
            const key = row?.keys?.[0] ?? '';
            return { id: key, key, ...rowMetrics(row, hasPosition) };
        })
        .sort((a, b) => b.impressions - a.impressions || (a.key < b.key ? -1 : 1));
}

/**
 * One selected query, read under the rule: its evaluation's state, or why it has none yet.
 *
 * A query that failed or has not been evaluated is never shown as "no overlap" — a request that did
 * not come back is not evidence of a single URL, and an evaluation nobody ran is not a clean bill.
 */
export function readEvaluation(scope, evaluation, rule) {
    if (!evaluation) return { state: STATE.PENDING };
    if (evaluation.status === 'loading') return { state: STATE.LOADING };
    if (evaluation.status === 'error') return { state: STATE.FAILED, error: evaluation.error };

    return {
        ...evaluatePages(evaluation.rows, rule, reportsPosition(scope.searchType)),
        capped: evaluation.capped,
    };
}

/**
 * Every selected query's evaluation read under the rule, once — blocks 02 and 03 and the export
 * all read from this, so a candidate's row and the evidence beneath it cannot disagree.
 */
export function readSelection(scope, selected, evaluations, rule) {
    return Object.fromEntries(
        selected.map((query) => [query, readEvaluation(scope, evaluations[query], rule)]),
    );
}

/** Block 02's rows: every selected query, in the order it was selected. */
export function candidateRows(scope, selected, reads) {
    return selected.map((query) => {
        const read = reads[query];
        const evaluated = !!read.pages;

        return {
            id: query,
            key: query,
            state: read.state,
            error: read.error ?? null,
            capped: !!read.capped,
            retrievedUrls: evaluated ? read.pages.length : null,
            qualifyingUrls: evaluated ? read.qualifying : null,
            impressions: evaluated ? read.total : null,
            largestShare: evaluated ? read.largestShare : null,
            navigation: navigationReason(query, scope.siteUrl),
        };
    });
}

/** The review state in words: the rule's reading, and the flags beside it. */
export function stateLabel(t, row) {
    const state =
        row.state === STATE.FAILED
            ? t('tools.cannibalization.states.failed', {
                  reason: row.error ?? t('global.something_went_wrong'),
              })
            : t(`tools.cannibalization.states.${row.state}`);

    const parts = [state];
    if (row.capped) parts.push(t('tools.cannibalization.states.capped'));
    if (row.navigation) {
        parts.push(t('tools.cannibalization.states.navigation', { term: row.navigation }));
    }
    return parts.join(' · ');
}

export function filterByReview(rows, review) {
    return review === REVIEW.NAVIGATION ? rows.filter((row) => !!row.navigation) : rows;
}

/** The counts the method and coverage blocks state, so the two can never disagree. */
export function coverageCounts(rows, shortlistSize) {
    const count = (states) => rows.filter((row) => states.includes(row.state)).length;
    const evaluated = count([STATE.OVERLAP, STATE.BELOW, STATE.NO_IMPRESSIONS]);

    return {
        retrieved: shortlistSize,
        selected: rows.length,
        evaluated,
        overlap: count([STATE.OVERLAP]),
        failed: count([STATE.FAILED]),
        pending: count([STATE.PENDING, STATE.LOADING]),
        flagged: rows.filter((row) => !!row.navigation).length,
        // Every retrieved query that was not evaluated successfully is untested — including the
        // selected ones still waiting or failed — and the coverage block says so in words.
        untested: Math.max(0, (shortlistSize ?? 0) - evaluated),
    };
}

/** A URL's own performance under this scope: Pages, Queries & Changes on its queries view. */
export function pagePerformanceLink(scope, url) {
    return pagesQueriesLink(scope, scope.current, {
        view: 'queries',
        rules: [{ dimension: 'page', operator: 'equals', expression: url }],
    });
}

export function inspectionLink(scope, url) {
    return urlInspectionLink(scope.siteUrl, url);
}
